import { EPSILON } from './../constants';
const isNearZero = (value) => Math.abs(value) < EPSILON;
const isDefined = (obj) => typeof obj !== 'undefined';
const defined = isDefined;
const isFunction = (obj) => typeof obj === 'function';
const isUndefined = (obj) => typeof obj === 'undefined' || obj === null;
/**
 * Returns whether the given object is an object or a value.
 */
const isObject = (obj) => obj === Object(obj);
/**
 * Returns whether the object has a property with the given name.
 */
const has = (obj, key) => Object.hasOwnProperty.call(obj, key);
/**
 * Returns whether the given object is a string.
 */
const isString = (obj) => {
    return Object.prototype.toString.call(obj) === '[object String]';
};
const isBoolean = (obj) => {
    return Object.prototype.toString.call(obj) === '[object Boolean]';
};
const isType = (obj, type) => {
    return Object.prototype.toString.call(obj) === '[object ' + type + ']';
};
/**
 * Returns whether the given object is a number.
 */
const isNumber = (obj) => !isNaN(parseFloat(obj)) && isFinite(obj);
/**
 * Return whether the given object (array or dictionary).
 */
const isEmpty = (obj) => {
    if (obj === null) {
        return true;
    }
    if (Array.isArray(obj) || isString(obj)) {
        return obj.length === 0;
    }
    for (const key in obj) {
        if (has(obj, key)) {
            return false;
        }
    }
    return true;
};
const simpleExtend = (destination, source) => {
    if (!isObject(source)) {
        return;
    }
    for (const name in source) {
        if (name) {
            destination[name] = source[name];
        }
    }
};
/**
 * Returns an array of the specified size and with each entry set to the given value.
 *
 * @param size
 * @param value
 * @returns {Array}
 */
const initArray = (size, value) => {
    const array = [];
    for (let i = 0; i < size; ++i) {
        array[i] = value;
    }
    return array;
};
const serializePoints = (points) => {
    const res = [];
    for (let i = 0; i < points.length; i++) {
        const p = points[i];
        res.push(p.x + ';' + p.y);
    }
    return res.join(';');
};
const deserializePoints = (s) => {
    const v = s.split(';');
    const points = [];
    if (v.length % 2 !== 0) {
        throw new Error('Not an array of points.');
    }
    for (let i = 0; i < v.length; i += 2) {
        points.push({
            x: parseInt(v[i], 10),
            y: parseInt(v[i + 1], 10)
        });
    }
    return points;
};
/**
 * Returns an integer within the given bounds.
 *
 * @param lower The inclusive lower bound.
 * @param upper The exclusive upper bound.
 * @returns {number}
 */
const randomInteger = (lower, upper) => {
    const random = Math.floor(Math.random() * upper) + lower;
    return parseInt(random.toString(), 10);
};
/*
 Depth-first traversal of the given node.
 */
const DFT = (el, func) => {
    func(el);
    if (el.childNodes) {
        for (let i = 0; i < el.childNodes.length; i++) {
            const item = el.childNodes[i];
            DFT(item, func);
        }
    }
};
/*
 Returns the angle in degrees for the given matrix
 */
const getMatrixAngle = (m) => {
    if (m === null || m.d === 0) {
        return 0;
    }
    return Math.atan2(m.b, m.d) * 180 / Math.PI;
};
/*
 Returns the scaling factors for the given matrix.
 */
const getMatrixScaling = (m) => {
    const sX = Math.sqrt(m.a * m.a + m.c * m.c);
    const sY = Math.sqrt(m.b * m.b + m.d * m.d);
    return [sX, sY];
};
const findRadian = (start, end) => {
    if (start === end) {
        return 0;
    }
    const sngXComp = end.x - start.x;
    const sngYComp = start.y - end.y;
    const atan = Math.atan(sngXComp / sngYComp);
    if (sngYComp >= 0) {
        return sngXComp < 0 ? atan + (2 * Math.PI) : atan;
    }
    return atan + Math.PI;
};
const sign = (num) => {
    return num ? num < 0 ? -1 : 1 : 0;
};
const findAngle = (center, end) => {
    return findRadian(center, end) * 180 / Math.PI;
};
const forEach = (arr, iterator, thisRef) => {
    for (let i = 0; i < arr.length; i++) {
        iterator.call(thisRef, arr[i], i, arr);
    }
};
const getAny = (arr, predicate) => {
    for (let i = 0; i < arr.length; ++i) {
        if (predicate(arr[i])) {
            return arr[i];
        }
    }
    return null;
};
const remove = (arr, what) => {
    let ax = arr.indexOf(what);
    while (ax !== -1) {
        arr.splice(ax, 1);
        ax = arr.indexOf(what);
    }
    return arr;
};
const contains = (arr, obj) => {
    return (arr || []).includes(obj);
};
const indexOf = (arr, what) => {
    return arr.indexOf(what);
};
const inArray = (what, arr) => {
    return arr.indexOf(what);
};
const grep = (arr, predicate) => arr.filter(predicate);
const fold = (list, iterator, acc, context) => {
    let initial = acc !== undefined;
    for (let i = 0; i < list.length; i++) {
        const value = list[i];
        if (!initial) {
            acc = value;
            initial = true;
        }
        else {
            acc = iterator.call(context, acc, value, i, list);
        }
    }
    if (!initial) {
        throw new Error('Reduce of empty array with no initial value');
    }
    return acc;
};
const find = (arr, iterator, context) => {
    return arr.find(iterator.bind(context)) || undefined;
};
const first = (arr, constraint, context) => {
    if (arr.length === 0) {
        return null;
    }
    if (isUndefined(constraint)) {
        return arr[0];
    }
    return find(arr, constraint, context);
};
/**
 * Inserts the given element at the specified position and returns the result.
 */
const insert = (arr, element, position) => {
    arr.splice(position, 0, element);
    return arr;
};
const all = (arr, iterator, context) => {
    let result = true;
    let value;
    for (let i = 0; i < arr.length; i++) {
        value = arr[i];
        result = result && iterator.call(context, value, i, arr);
        if (!result) {
            break;
        }
    }
    return result;
};
const clear = (arr) => {
    arr.splice(0, arr.length);
};
const bisort = (a, b, sortfunc) => {
    if (isUndefined(a)) {
        throw new Error('First array is not specified.');
    }
    if (isUndefined(b)) {
        throw new Error('Second array is not specified.');
    }
    if (a.length !== b.length) {
        throw new Error('The two arrays should have equal length');
    }
    const points = [];
    for (let i = 0; i < a.length; i++) {
        points.push({ x: a[i], y: b[i] });
    }
    if (isUndefined(sortfunc)) {
        points.sort((m, n) => m.x - n.x);
    }
    else {
        points.sort((m, n) => sortfunc(m.x, n.x));
    }
    clear(a);
    clear(b);
    for (let i = 0; i < points.length; i++) {
        a.push(points[i].x);
        b.push(points[i].y);
    }
};
const addRange = (arr, range) => {
    arr.push(...range);
};
const noop = () => { };
export { isNearZero, isDefined, defined, isUndefined, isFunction, isObject, has, isString, isBoolean, isType, isNumber, isEmpty, simpleExtend, initArray, serializePoints, deserializePoints, randomInteger, DFT, getMatrixAngle, getMatrixScaling, findRadian, sign, findAngle, forEach, getAny, remove, contains, indexOf, inArray, grep, fold, find, first, insert, all, clear, bisort, addRange, noop };
